<template>
  <v-container class="limitation-form pa-0">
    <!-- Who / why -->
    <v-row dense>
      <v-col cols="12" md="8">
        <v-autocomplete
          v-model="producers"
          :items="$store.state.producers.all"
          item-title="display_name"
          item-value="pk"
          :label="$filters.capitalize($t('producers.label', newLimitation ? 10 : 1))"
          :multiple="newLimitation"
          :chips="newLimitation"
          :closable-chips="newLimitation"
          prepend-inner-icon="mdi-wind-turbine"
          variant="outlined"
          density="compact"
          hide-details="auto"
          clearable
          return-object
          @update:modelValue="updateProducer"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                {{ $filters.capitalize($t("search_for", { noun: $t("producers.label", 1) })) }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedOrigin"
          :label="$filters.capitalize($t('limitations.origin.label'))"
          :items="origin_options"
          item-title="label"
          return-object
          prepend-inner-icon="mdi-tag-outline"
          variant="outlined"
          density="compact"
          hide-details="auto"
          @update:modelValue="updateOrigin"
        ></v-select>
      </v-col>
    </v-row>

    <!-- When -->
    <v-row dense class="mt-3">
      <v-col cols="12" md="6">
        <div class="text-caption text-medium-emphasis mb-1">
          {{ $filters.capitalize($t("limitations.start")) }}
        </div>
        <VueDatePicker
          v-model="start"
          :clearable="false"
          :locale="$store.getters.language"
          :teleport="true"
          time-picker-inline
          :minutes-increment="5"
          format="dd/MM/yyyy HH:mm"
          @update:model-value="setStart"
        ></VueDatePicker>
      </v-col>
      <v-col cols="12" md="6">
        <div class="text-caption text-medium-emphasis mb-1">
          {{ $filters.capitalize($t("limitations.end")) }}
        </div>
        <VueDatePicker
          v-model="end"
          :disabled="noEnd"
          :clearable="false"
          :locale="$store.getters.language"
          :teleport="true"
          :min-date="start"
          time-picker-inline
          :minutes-increment="5"
          format="dd/MM/yyyy HH:mm"
          :placeholder="$filters.capitalize($t('limitations.no_end'))"
          @update:model-value="setEnd"
        ></VueDatePicker>
        <v-checkbox
          v-model="noEnd"
          density="compact"
          hide-details
          color="primary"
          :label="$filters.capitalize($t('limitations.no_end'))"
          @update:modelValue="toggleNoEnd"
        ></v-checkbox>
      </v-col>
    </v-row>

    <!-- What -->
    <v-row dense class="mt-3">
      <v-col cols="12" md="4">
        <v-text-field
          v-model.number="editedItem.power_limit"
          type="number"
          min="0"
          :label="$filters.capitalize($t('limitations.power_limit'))"
          prepend-inner-icon="mdi-flash-outline"
          variant="outlined"
          density="compact"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="8">
        <v-textarea
          v-model="editedItem.comment"
          :label="$filters.capitalize($t('limitations.comment'))"
          variant="outlined"
          density="compact"
          rows="2"
          auto-grow
          hide-details="auto"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-switch
      v-model="editedItem.impact_dispo"
      color="primary"
      inset
      hide-details
      class="mt-2"
      :label="$filters.capitalize($t('limitations.impact_dispo.label'))"
    ></v-switch>
    <div class="text-caption text-medium-emphasis ml-1">
      {{ editedItem.impact_dispo
        ? $t("limitations.impact_dispo.true")
        : $t("limitations.impact_dispo.false") }}
    </div>
  </v-container>
</template>

<script>
import moment from "moment";
import { isNone } from "@/functions";

// What the API expects (see LimitationSerializer.to_internal_value): a
// naive ISO datetime, read as Europe/Paris
const API_FORMAT = "YYYY-MM-DDTHH:mm";

export default {
  methods: {
    updateProducer(item) {
      if (this.newLimitation) {
        this.editedItem.producer_ids = isNone(item) ? [] : item;
        this.editedItem.producer = undefined;
      } else {
        this.editedItem.producer_id = isNone(item) ? undefined : item.pk;
        this.editedItem.producer = undefined;
      }
    },
    updateOrigin(item) {
      if (isNone(item)) return;
      this.editedItem.origin = item;
      this.editedItem.origin_id = item.pk;
    },
    setStart(value) {
      this.$store.dispatch("limitations/setStart", this.toApi(value));
    },
    setEnd(value) {
      this.$store.dispatch("limitations/setEnd", this.toApi(value));
    },
    toggleNoEnd(checked) {
      if (checked) {
        this.end = null;
      } else {
        // back to a bounded limitation: one hour after the start by default
        this.end = moment(this.start).add(1, "hour").toDate();
      }
      this.setEnd(this.end);
    },
    toApi(value) {
      return isNone(value) ? null : moment(value).format(API_FORMAT);
    },
  },
  created() {
    this.origin_options = this.$store.state.limitation_origins.items.filter(
      (item) => item.user_input_allowed === true
    );
    // Keep the origin of an existing limitation, default to the first one
    const current = this.editedItem.origin;
    this.selectedOrigin =
      this.origin_options.find((o) => current && o.pk === current.pk) ||
      this.origin_options[0];
    this.updateOrigin(this.selectedOrigin);

    // Producer(s): the edited producer when editing, none when creating
    const producer = this.$store.getters.dataEdit("producers");
    if (this.newLimitation) {
      this.producers = [];
    } else {
      this.producers = producer && producer.pk ? { ...producer } : null;
    }

    // Period: the edited values, or now with no end for a new limitation
    const start = this.editedItem.start;
    this.start = isNone(start) || this.newLimitation
      ? moment().startOf("hour").toDate()
      : moment(start).toDate();
    const end = this.editedItem.end;
    this.noEnd = isNone(end);
    this.end = this.noEnd ? null : moment(end).toDate();
    this.setStart(this.start);
    this.setEnd(this.end);
  },
  computed: {
    editedItem() {
      return this.$store.getters.dataEdit(this.moduleName);
    },
    newLimitation() {
      return isNaN(this.$store.state.limitations.edit.pk);
    },
  },
  data() {
    return {
      moduleName: "limitations",
      producers: null,
      selectedOrigin: undefined,
      origin_options: [],
      start: null,
      end: null,
      noEnd: true,
    };
  },
};
</script>
